import { useEffect, type RefObject } from "react";

const CANDIDATE_SELECTOR = [
  "td", "th", ".ui-truncate", "[class*='-name']", "[class*='-title']",
  "[class*='-email']", "[class*='-description']", "[class*='-subtitle']",
  "[class*='-label']", "[class*='-value']", ".bl-finding > span",
].join(",");

/** Reveals values shortened by a dense layout, including dynamic table rows. */
export function useOverflowTooltip(rootRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let tooltip: HTMLDivElement | null = null;
    let owner: HTMLElement | null = null;

    const hide = (): void => {
      tooltip?.remove();
      tooltip = null;
      owner = null;
    };

    const show = (target: EventTarget | null): void => {
      if (!(target instanceof Element)) return;
      const candidate = target.closest<HTMLElement>(CANDIDATE_SELECTOR);
      if (!candidate || !root.contains(candidate)) return;
      if (candidate.matches("td,th") && candidate.querySelector("button,input,select,textarea")) return;

      const clipped = candidate.scrollWidth > candidate.clientWidth + 1 || candidate.scrollHeight > candidate.clientHeight + 1;
      if (!clipped || candidate === owner) return;
      const value = (candidate.getAttribute("aria-label") || candidate.textContent || "").replace(/\s+/g, " ").trim();
      if (!value) return;
      hide();

      tooltip = document.createElement("div");
      tooltip.className = "global-overflow-tooltip";
      tooltip.textContent = value;
      tooltip.setAttribute("role", "tooltip");
      document.body.appendChild(tooltip);
      owner = candidate;

      const rect = candidate.getBoundingClientRect();
      const tip = tooltip.getBoundingClientRect();
      tooltip.style.left = `${Math.min(Math.max(8, rect.left), window.innerWidth - tip.width - 8)}px`;
      const preferredTop = rect.top >= tip.height + 10 ? rect.top - tip.height - 6 : rect.bottom + 6;
      tooltip.style.top = `${Math.min(preferredTop, window.innerHeight - tip.height - 8)}px`;
    };

    const onOver = (event: MouseEvent): void => show(event.target);
    const onOut = (event: MouseEvent): void => {
      if (owner?.contains(event.relatedTarget as Node | null)) return;
      hide();
    };
    const onFocusIn = (event: FocusEvent): void => show(event.target);

    root.addEventListener("mouseover", onOver);
    root.addEventListener("mouseout", onOut);
    root.addEventListener("focusin", onFocusIn);
    root.addEventListener("focusout", hide);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      hide();
      root.removeEventListener("mouseover", onOver);
      root.removeEventListener("mouseout", onOut);
      root.removeEventListener("focusin", onFocusIn);
      root.removeEventListener("focusout", hide);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [rootRef]);
}
